/**
 * NO-767 restore on return. Opening a Past row leaves one read-once, per-tab
 * note of which row it was, so coming back to that week (browser Back, the
 * in-app Back link, the breadcrumb, or a same-tab refresh) can scroll to the row
 * and focus it. It holds only the week and the opaque Appointment id that the
 * detail URL already shows: never a Patient name or any Appointment content, and
 * never the admin's RD selection, which is URL-only and is never persisted,
 * defaulted, or restored (ADR-0052). The page clears it whenever the selection
 * changes, so it cannot cross selections. It lives in sessionStorage only, and
 * every storage access falls back to memory when storage is missing or throws.
 */
export interface AppointmentRestoreRecord {
  week: string;
  appointmentId: string;
}

export interface RestoreRecordStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const STORAGE_KEY = "always-ai.appointments.past-restore";
/** An older record is stale: the RD has moved on. */
export const RESTORE_RECORD_MAX_AGE_MS = 30 * 60_000;

export interface AppointmentRestoreRecords {
  write(record: AppointmentRestoreRecord): void;
  /** The record for exactly this week, removed as it is read. */
  consume(match: { week: string }): AppointmentRestoreRecord | null;
  /** Whether a record for exactly this week is waiting. */
  peek(match: { week: string }): boolean;
  clear(): void;
}

function sessionStorageOrNull(): RestoreRecordStorage | null {
  return typeof sessionStorage === "undefined" ? null : sessionStorage;
}

export function createAppointmentRestoreRecords(
  options: {
    storage?: () => RestoreRecordStorage | null;
    clock?: () => number;
  } = {}
): AppointmentRestoreRecords {
  const storage = options.storage ?? sessionStorageOrNull;
  const clock = options.clock ?? Date.now;
  const memory = new Map<string, string>();
  // When it was written, kept beside the record rather than in it. After a
  // reload the time is unknown and the record is honoured once.
  let writtenAt: number | null = null;

  const read = (): string | null => {
    let stored: string | null = null;

    try {
      stored = storage()?.getItem(STORAGE_KEY) ?? null;
    } catch {
      // Storage refused: fall through to memory.
    }

    if (stored !== null) return stored;

    return memory.get(STORAGE_KEY) ?? null;
  };

  const save = (value: string): void => {
    memory.set(STORAGE_KEY, value);

    try {
      storage()?.setItem(STORAGE_KEY, value);
    } catch {
      // Memory still holds it for this page.
    }
  };

  const remove = (): void => {
    memory.delete(STORAGE_KEY);
    writtenAt = null;

    try {
      storage()?.removeItem(STORAGE_KEY);
    } catch {
      // Nothing more to clear.
    }
  };

  const current = (): AppointmentRestoreRecord | null => {
    const stale = writtenAt !== null && clock() - writtenAt > RESTORE_RECORD_MAX_AGE_MS;
    const record = stale ? null : parseRestoreRecord(read());
    if (!record) remove();

    return record;
  };

  return {
    write(record) {
      save(JSON.stringify(restoreRecordFields(record)));
      writtenAt = clock();
    },
    consume(match) {
      const record = current();
      if (!record || !matchesRestoreRecord(record, match)) return null;

      remove();

      return record;
    },
    peek(match) {
      const record = current();

      return record !== null && matchesRestoreRecord(record, match);
    },
    clear: remove,
  };
}

function restoreRecordFields(
  record: AppointmentRestoreRecord
): AppointmentRestoreRecord {
  return { week: record.week, appointmentId: record.appointmentId };
}

export function matchesRestoreRecord(
  record: AppointmentRestoreRecord,
  match: { week: string }
): boolean {
  return record.week === match.week;
}

function parseRestoreRecord(value: string | null): AppointmentRestoreRecord | null {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as Partial<
      Record<keyof AppointmentRestoreRecord, unknown>
    >;
    const { week, appointmentId } = parsed;
    const hasFields = typeof week === "string" && typeof appointmentId === "string";

    return hasFields ? { week, appointmentId } : null;
  } catch {
    return null;
  }
}

/**
 * Only a plain primary-button activation opens the row in this tab: a modifier
 * key or another button opens it elsewhere, and this tab never returns to it.
 */
export function isPlainPrimaryActivation(event: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  defaultPrevented: boolean;
}): boolean {
  const hasModifier = [event.metaKey, event.ctrlKey, event.shiftKey, event.altKey].some(
    Boolean
  );

  return event.button === 0 && !hasModifier && !event.defaultPrevented;
}

/** The page's per-tab records. */
export const appointmentRestoreRecords = createAppointmentRestoreRecords();
